import { useEffect, useMemo, useState } from "react";
import { Button, Card, Cluster, FormField, Grid, Input, LabelWithHint, Modal, Select, Stack } from "@salesapp/ui";
import { TipHeading, TipKicker } from "./AdminTip";
import { api, ApiError } from "./api";
import { formatUtc } from "./format";
import { Flash } from "./Flash";

interface ErrorSummary {
  last_24h: number;
  last_24h_5xx: number;
  last_90d: number;
}

interface ErrorEvent {
  id: string;
  error_id: string;
  created_at: string | null;
  environment: string;
  status_code: number;
  exception_type: string;
  message: string;
  path: string | null;
  method: string | null;
  source_ip: string | null;
  correlation_id: string | null;
  user_id: string | null;
  org_id: string | null;
  user_email: string | null;
  user_name: string | null;
  org_name: string | null;
  app: string | null;
  tab: string | null;
  surface: string | null;
  client_message: string;
}

interface ErrorDetail extends ErrorEvent {
  stack_trace: string;
  query_string: string | null;
  user_agent: string | null;
  is_platform_staff: boolean | null;
  request_headers: Record<string, string>;
  session_snapshot: Record<string, unknown>;
}

interface ErrorsResponse {
  summary: ErrorSummary;
  events: ErrorEvent[];
  total: number;
  retention_days: number;
}

function utcDay(offsetDays: number): string {
  return new Date(Date.now() + offsetDays * 86_400_000).toISOString().slice(0, 10);
}

function stackLabel(environment: string | null | undefined): string {
  const raw = (environment || "").trim().toLowerCase();
  if (raw === "dev" || raw === "development") return "DEV";
  if (raw === "prd" || raw === "prod" || raw === "production") return "PRD";
  if (raw === "test") return "TEST";
  return (environment || "UNKNOWN").trim().toUpperCase() || "UNKNOWN";
}

function personLabel(row: Pick<ErrorEvent, "user_name" | "user_email" | "user_id">): string {
  const name = row.user_name?.trim();
  const email = row.user_email?.trim();
  if (name && email) return `${name} <${email}>`;
  return email || name || row.user_id || "anonymous";
}

function companyLabel(row: Pick<ErrorEvent, "org_name" | "org_id">): string {
  const name = row.org_name?.trim();
  if (name && row.org_id) return `${name} (${row.org_id})`;
  return name || row.org_id || "—";
}

function errorsPath(filters: {
  status: string;
  environment: string;
  path: string;
  from: string;
  to: string;
}): string {
  const params = new URLSearchParams();
  params.set("limit", "100");
  if (filters.status) params.set("status_code", filters.status);
  if (filters.environment) params.set("environment", filters.environment);
  if (filters.path) params.set("path", filters.path);
  if (filters.from) params.set("created_from", filters.from);
  if (filters.to) params.set("created_to", filters.to);
  return `/operator/errors?${params.toString()}`;
}

export function MonitoringPage() {
  const [data, setData] = useState<ErrorsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState("");
  const [environment, setEnvironment] = useState("");
  const [pathInput, setPathInput] = useState("");
  const [path, setPath] = useState("");
  const [from, setFrom] = useState(() => utcDay(-90));
  const [to, setTo] = useState(() => utcDay(0));
  const [selected, setSelected] = useState<ErrorDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const defaultFrom = useMemo(() => utcDay(-90), []);
  const defaultTo = useMemo(() => utcDay(0), []);
  const filters = useMemo(
    () => ({ status, environment, path, from, to }),
    [status, environment, path, from, to],
  );
  const filtersDirty =
    status !== "" ||
    environment !== "" ||
    pathInput.trim() !== "" ||
    from !== defaultFrom ||
    to !== defaultTo;

  function clearFilters() {
    setStatus("");
    setEnvironment("");
    setPathInput("");
    setPath("");
    setFrom(utcDay(-90));
    setTo(utcDay(0));
  }

  useEffect(() => {
    const handle = window.setTimeout(() => setPath(pathInput.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [pathInput]);

  async function load() {
    setError(null);
    const row = await api.get<ErrorsResponse>(errorsPath(filters));
    setData(row);
  }

  useEffect(() => {
    let cancelled = false;
    async function refresh() {
      try {
        await load();
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load errors.");
      }
    }
    void refresh();
    return () => {
      cancelled = true;
    };
  }, [filters]);

  async function copyError() {
    if (!selected) return;
    const lines = [
      `${selected.status_code} ${selected.exception_type}`,
      `Reference: ${selected.error_id}`,
      `When: ${formatUtc(selected.created_at)} (${stackLabel(selected.environment)})`,
      `Message: ${selected.message}`,
      `Request: ${selected.method ?? ""} ${selected.path ?? ""}${selected.query_string ? `?${selected.query_string}` : ""}`.trim(),
      `User: ${personLabel(selected)}`,
      `Company: ${companyLabel(selected)}`,
      ...(selected.correlation_id ? [`Correlation: ${selected.correlation_id}`] : []),
      "",
      selected.stack_trace,
    ];
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Could not copy to the clipboard. Select the stack text and copy it by hand.");
    }
  }

  async function openDetail(errorId: string) {
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const row = await api.get<ErrorDetail>(`/operator/errors/${encodeURIComponent(errorId)}`);
      setSelected(row);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load error detail.");
    } finally {
      setBusy(false);
    }
  }

  const summary = data?.summary;
  const events = data?.events ?? [];

  return (
    <Stack gap={4}>
      <Cluster gap={3} align="center" justify="between">
        <Stack gap={2}>
          <h2>Monitoring</h2>
          <p className="admin-muted">
            Server errors with full stack traces, request context, and session snapshots.
            Clients never see this — they get a generic message and a reference id.
          </p>
        </Stack>
      </Cluster>
      {error && <Flash tone="error">{error}</Flash>}
      <Grid columns={3} gap={3}>
        <Card>
          <Stack gap={2}>
            <TipKicker
              label="Last 24 hours"
              tip="Captured server errors in the last 24 hours across all environments, ignoring the table filters below."
            />
            <strong>{summary?.last_24h ?? "—"}</strong>
          </Stack>
        </Card>
        <Card>
          <Stack gap={2}>
            <TipKicker
              label="5xx last 24 hours"
              tip="Subset of the last-24h count that returned HTTP 5xx. Use this as the primary health signal."
            />
            <strong>{summary?.last_24h_5xx ?? "—"}</strong>
          </Stack>
        </Card>
        <Card>
          <Stack gap={2}>
            <TipKicker
              label="Last 90 days"
              tip="Total captured errors in the retention window. Matches the default date range on this page."
            />
            <strong>{summary?.last_90d ?? "—"}</strong>
          </Stack>
        </Card>
      </Grid>
      <Card>
        <Cluster gap={3} align="end" className="admin-monitor-filters">
          <FormField
            label="From date"
            tooltip="Inclusive start of the error list (UTC calendar day). Summary cards above stay fixed to 24h / 90d."
          >
            <Input
              type="date"
              aria-label="From date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          </FormField>
          <FormField
            label="To date"
            tooltip="Inclusive end of the error list (UTC calendar day)."
          >
            <Input
              type="date"
              aria-label="To date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
            />
          </FormField>
          <FormField
            label="Status"
            tooltip="Filter the table by HTTP status. Leave All to include every captured code."
          >
            <Select aria-label="Filter status" value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">All</option>
              <option value="500">500</option>
              <option value="502">502</option>
              <option value="503">503</option>
              <option value="504">504</option>
            </Select>
          </FormField>
          <FormField
            label="Environment"
            tooltip="Limit to PRD, DEV, or TEST stacks. Empty includes every environment."
          >
            <Select
              aria-label="Filter environment"
              value={environment}
              onChange={(event) => setEnvironment(event.target.value)}
            >
              <option value="">All</option>
              <option value="production">PRD</option>
              <option value="dev">DEV</option>
              <option value="test">TEST</option>
            </Select>
          </FormField>
          <FormField
            label="Path"
            tooltip="Substring match on the request path (for example /api/v1/). Debounced as you type."
          >
            <div className="admin-monitor-path-field">
              <Input
                aria-label="Filter path"
                value={pathInput}
                onChange={(event) => setPathInput(event.target.value)}
                placeholder="/api/v1/"
              />
            </div>
          </FormField>
          <FormField
            label="Filters"
            tooltip="Reset dates, status, environment, and path to the page defaults."
          >
            <Button type="button" variant="secondary" disabled={!filtersDirty} onClick={clearFilters}>
              Clear
            </Button>
          </FormField>
        </Cluster>
      </Card>
      <Card>
        <Stack gap={3}>
          <TipHeading
            label="Captured errors"
            tip="Rows matching the filters. Open a row for stack trace, client message, and session context. Clients only see the reference id."
          />
          <table>
            <thead>
              <tr>
                <th>When (UTC)</th>
                <th>Env</th>
                <th>Status</th>
                <th>Type</th>
                <th>App / tab</th>
                <th>Path</th>
                <th>User</th>
                <th>Company</th>
                <th>Reference</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {events.map((row) => (
                <tr key={row.id}>
                  <td className="admin-readonly">{formatUtc(row.created_at)}</td>
                  <td>{stackLabel(row.environment)}</td>
                  <td>{row.status_code}</td>
                  <td>{row.exception_type}</td>
                  <td>{row.surface || "—"}</td>
                  <td><code>{row.method} {row.path}</code></td>
                  <td>{personLabel(row)}</td>
                  <td>{companyLabel(row)}</td>
                  <td><code>{row.error_id}</code></td>
                  <td>
                    <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => openDetail(row.error_id)}>
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {events.length === 0 && <p className="admin-muted">No captured errors in this range.</p>}
        </Stack>
      </Card>
      <Modal
        isOpen={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? `${selected.status_code} · ${selected.exception_type}` : "Error"}
        footer={(
          <Cluster gap={2}>
            <Button type="button" variant="secondary" onClick={() => void copyError()}>
              {copied ? "Copied" : "Copy error"}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setSelected(null)}>
              Close
            </Button>
          </Cluster>
        )}
      >
        {selected && (
          <Stack gap={3}>
            <p className="admin-muted">
              Reference <code>{selected.error_id}</code> · {formatUtc(selected.created_at)} · {stackLabel(selected.environment)}
            </p>
            <p>
              <LabelWithHint
                label="Client saw"
                tooltip="Safe message shown to the end user. They never see the stack or internal exception text."
              />
              {": "}
              {selected.client_message}
            </p>
            <p>
              <LabelWithHint
                label="Message"
                tooltip="Server-side exception message for operators. Not shown to the client."
              />
              {": "}
              {selected.message}
            </p>
            <p className="admin-muted">
              {selected.method} {selected.path}
              {selected.query_string ? `?${selected.query_string}` : ""}
              {selected.source_ip ? ` · ${selected.source_ip}` : ""}
            </p>
            <p>
              <LabelWithHint
                label="App"
                tooltip="Product surface that raised the error when the client reported one."
              />
              {": "}
              {selected.app || "—"}
            </p>
            <p>
              <LabelWithHint
                label="Tab"
                tooltip="UI tab or route context from the client snapshot, when available."
              />
              {": "}
              {selected.tab || "—"}
            </p>
            <p>
              <LabelWithHint
                label="User"
                tooltip="Signed-in person on the request, or anonymous if there was no session."
              />
              {": "}
              {personLabel(selected)}
            </p>
            <p>
              <LabelWithHint
                label="Company"
                tooltip="Tenant org on the request when the call was org-scoped."
              />
              {": "}
              {companyLabel(selected)}
            </p>
            {selected.correlation_id ? (
              <p className="admin-muted">Correlation · {selected.correlation_id}</p>
            ) : null}
            <pre className="admin-stack">{selected.stack_trace}</pre>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
